import { useAtom }                              from "jotai";
import React, { useCallback, useRef, useState } from "react";
import useSWR                                   from "swr";
import useSWRInfinite                           from "swr/infinite";

import { useGetLoggedIn, useGetProfileData }                        from "_common/hooks/auth";
import { useGetDarkMode, useSetLoginPopup }                         from "_common/hooks/global";
import Toast                                                        from "_common/toast";
import { followersAtom }                                            from "_jotai/userProfile.js";
import { getCommentsOnFeed, getReactionsOnFeed, postCommentOnFeed } from "_services/api/feed";

import Presentation from "./presentation";

const fetchComments = async params => {
	const { id, pageNo, active } = params;

	if ( !active )
		return;

	const res = await getCommentsOnFeed ( { feedId: parseInt ( id ), pageNo, pageSize: 10 } );

	return res;
};

const fetchLikes = async params => {
	const { id, pageNo, active } = params;

	if ( !active )
		return;

	const res = await getReactionsOnFeed ( { feedId: parseInt ( id ), pageNo, pageSize: 10, reaction: "AGREE" } );

	return res;
};

const component = ( {
	totalLength,
	handleImageModalClose,
	modalStatus,
	initialTabType,
	selectedImage,
	imageList,
	setSelectedImage,
	updateDeletedPost,
	verticalFeedType
} ) => {

	const { index, image }              = selectedImage;
	const [ anchorEl, setAnchorEl ]     = useState ( null );
	const [ showDialog, setShowDialog ] = useState ( false );
	const [ activeTab, setActiveTab ]   = useState ( initialTabType === "like" ? 1 : 0 );
	const userProfile                   = useGetProfileData ();
	const isLoggedIn                    = useGetLoggedIn ();
	const darkMode                      = useGetDarkMode ();
	const [ followers ]                 = useAtom ( followersAtom );
	const setLoginPopup                 = useSetLoginPopup ();

	const [ displayEmoji, setDisplayEmoji ] = useState ( false );
	const [ focusState, setFocusState ]     = useState ( false );
	const textInput                         = useRef ( null );

	const [ ownCommentsList, setOwnCommentList ] = useState ( [] );

	const { data: commentCount, mutate: setCommentCount } = useSWR ( { key: "comment-count", id: image?.id }, null, { fallbackData: image?.commentCount || 0 } );

	const { data: comments, error: commentError, size: commentSize, setSize: setCommentSize } = useSWRInfinite (
		index => {
			return { key: "image-comments", pageNo: index + 1, id: image?.id, active: activeTab === 0 ? true : false };
		},
		fetchComments,
		{ revalidateFirstPage: false }
	);

	const { data: likes, error: likeError, size: likeSize, setSize: setLikeSize } = useSWRInfinite (
		index => {
			return { key: "image-likes", pageNo: index + 1, id: image?.id, active: activeTab === 1 ? true : false };
		},
		fetchLikes
	);

	const handleClick = event => {
		setAnchorEl ( event.currentTarget );
	};

	React.useEffect ( () => {
		setOwnCommentList ( [] );

		return () => {
			setOwnCommentList ( [] );
		};
	}, [] );

	const handleClose = () => {
		setAnchorEl ( null );
	};

	const handleReport = () => {
		if ( !isLoggedIn )
			return setLoginPopup ( true );

		setShowDialog ( true );
		handleClose ();
	};

	// state for infinite call logic
	let __comments                 = [].concat.apply ( [], comments );
	const isLoadingInitialComments = !comments && !commentError;
	const isCommentsEmpty          = comments?.[commentSize - 1]?.length === 0;
	const isLoadingMoreComments    = isLoadingInitialComments || ( commentSize > 0 && comments && typeof comments[commentSize - 1] === "undefined" );

	// state for infinite call logic
	let __likes                 = [].concat.apply ( [], likes );
	const isLoadingInitialLikes = !likes && !likeError;
	const isLikesEmpty          = likes?.[likeSize - 1]?.length === 0;
	const isLoadingMoreLikes    = isLoadingInitialLikes || ( likeSize > 0 && likes && typeof likes[likeSize - 1] === "undefined" );

	const [ commentInput, setCommentInput ] = useState ( "" );

	const currentTabParams = {
		data       : activeTab === 0 ? [ ...ownCommentsList, ...__comments ] : __likes,
		isFetching : activeTab === 0 ? isLoadingMoreComments : isLoadingMoreLikes,
		isDepleted : activeTab === 0 ? isCommentsEmpty : isLikesEmpty,
		pageNo     : activeTab === 0 ? commentSize : likeSize
	};

	const addCommentToPost = async e => {
		e.preventDefault ();

		if ( !isLoggedIn )
			return setLoginPopup ( true );

		if ( !commentInput ) return false;

		try {
			const res = await postCommentOnFeed ( {
				commentMessage : commentInput,
				feedId         : parseInt ( image.id )
			} );

			setOwnCommentList ( prevState => [ {
				id               : res.comment_id,
				commentMessage   : commentInput,
				sportsFanDetails : {
					name  : userProfile.name,
					photo : userProfile.photo,
					id    : userProfile.id
				},
				sportsFanId: userProfile.id
			}, ...prevState ] );

			setCommentCount ( res.reaction_count );
		} catch ( err ) {
			Toast.error ( err.displayMsg );
		}

		setCommentInput ( "" );
	};

	const handleTabChange = newVal => {
		setActiveTab ( Number ( newVal ) );
	};

	const handleOnCommentChange = e => {
		setCommentInput ( e.target.value );
	};

	const handleClickModal = direction => {

		if ( direction === "next" ) {
			setSelectedImage ( {
				index : index + 1,
				image : imageList[index + 1]
			} );
		}

		if ( direction === "back" ) {
			setSelectedImage ( {
				index : index - 1,
				image : imageList[index - 1]
			} );
		}
	};

	/* For Showing Emoji in text */
	const handleEmojiSelect = useCallback (
		e => {
			/*
				Splitting the id  on (‘-’), before adding ‘0x’ to each element
			*/
			let sym        = e.unified.split ( "-" );
			let codesArray = [];

			/*
				Here, unified is in Unicode (hex) so
				we need to add ‘0x’ in the front of the code for it to become an emoji
			*/
			sym.forEach ( el => codesArray.push ( "0x" + el ) );
			let emoji = String.fromCodePoint ( ...codesArray );

			setCommentInput ( commentInput + " " + emoji );
			textInput?.current?.focus ();
			setFocusState ( true );
		},
		[ commentInput, focusState, textInput ]
	);

	/* For Toggle Emoji Container */
	const handleToggleEmoji = () => {
		if ( !isLoggedIn ) {
			setLoginPopup ( true );

			return;
		}
		if ( displayEmoji && focusState )
			textInput?.current?.focus ();
		setDisplayEmoji ( prev => !prev );
	};

	const toggleFocusState = () => {
		if ( displayEmoji && focusState )
			textInput?.current?.focus ();
		setFocusState ( prev => !prev );
	};

	const fetchMoreComments = () => {
		setCommentSize ( commentSize + 1 );
	};

	const fetchMoreLikes = () => {
		setLikeSize ( likeSize + 1 );
	};

	return (
		<Presentation
			activeTab             = { activeTab }
			addCommentToPost      = { addCommentToPost }
			anchorEl              = { anchorEl }
			commentCount          = { commentCount }
			commentInput          = { commentInput }
			currentTabParams      = { currentTabParams }
			darkMode              = { darkMode }
			displayEmoji          = { displayEmoji }
			fetchComments         = { fetchMoreComments }
			fetchLikes            = { fetchMoreLikes }
			focusState            = { focusState }
			followers             = { followers }
			handleClick           = { handleClick }
			handleClickModal      = { handleClickModal }
			handleClose           = { handleClose }
			handleEmojiSelect     = { handleEmojiSelect }
			handleModalClose      = { handleImageModalClose }
			handleOnCommentChange = { handleOnCommentChange }
			handleReport          = { handleReport }
			handleTabChange       = { handleTabChange }
			handleToggleEmoji     = { handleToggleEmoji }
			image                 = { image }
			listStartCheck        = { index !== 0 }
			modalStatus           = { modalStatus }
			reachedListEnd        = { index !== totalLength - 1 }
			setShowDialog         = { setShowDialog }
			showDialog            = { showDialog }
			textInput             = { textInput }
			toggleFocusState      = { toggleFocusState }
			updateDeletedPost     = { updateDeletedPost }
			verticalFeedType      = { verticalFeedType }
		/>

	);
};

export default component;
